<template>
  <div
    data-component-group="business-form"
    data-component="account-managers"
  >
    <slot name="beforeForm" />

    <Can
      action="assign-manager"
      subject="business"
    >
      <div
        v-if="!isDisabled"
        class="component-header"
      >
        <v-row>
          <div
            xs12
            md4
          >
            <v-autocomplete
              v-model="manager"
              :items="managers"
              :loading="isLoadingManagers"
              prepend-inner-icon="contacts"
              label="Account managers"
              item-title="name"
              item-value="id"
              :return-object="true"
              required
              :readonly="isDisabled"
              :error-messages="managerErrors"
              v-model:search="managerSearch"
            />
          </div>
          <div
            xs12
            md4
          >
            <v-autocomplete
              v-model="country"
              :items="countries"
              prepend-inner-icon="public"
              label="Country"
              item-title="name"
              item-value="code"
              :return-object="true"
              required
              :readonly="isDisabled"
              :error-messages="countryErrors"
              @blur="v$.country.$touch()"
            />
          </div>
          <div
            xs12
            md4
          >
            <v-autocomplete
              v-model="assign"
              :items="assignItems"
              prepend-inner-icon="change_history"
              label="Assign"
              item-title="name"
              item-value="name"
              :return-object="true"
              required
              :readonly="isDisabled"
            />
          </div>
        </v-row>

        <div
          v-if="!isDisabled"
          class="component-actions"
        >
          <v-btn
            color="secondary"
            :loading="isLoading"
            @click="onUpdate"
          >
            <v-icon>contacts</v-icon>
            {{ triggerLabel }}
          </v-btn>
        </div>
      </div>
    </Can>

    <div class="component-body">
      <div class="item-grid-rows" :class="{ 'item-grid-rows--edit': !isDisabled && $ability.can('unassign-manager', 'business') }">
      <Removables
        v-if="hasItems"
        :items="accountManagers"
        :is-disabled="isDisabled || !$ability.can('unassign-manager', 'business')"
        :on-remove="onRemove"
      >
        <template
          v-slot:item="scope"

        >
          <div class="item-grid">
            <div class="d-flex align-center">
              <v-icon>
                public
              </v-icon>
              <span>{{ scope.item.manager_country.name }}</span>
            </div>
            <div class="d-flex align-center">
              <v-icon>
                contacts
              </v-icon>
              <router-link :to="{ name: 'internal-users-edit', params: { id: scope.item.manager.id } }" target="_blank">{{ scope.item.manager.firstname }} {{ scope.item.manager.lastname }}</router-link>
            </div>
            <div class="d-flex align-center">
              <v-icon>
                change_history
              </v-icon>
              <span>{{ scope.item.assign === 1 ? 'Yes' : 'No' }}</span>
            </div>
          </div>
        </template>
      </Removables>
      </div>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import { v1 as uuidv1 } from 'uuid';
  import Removables from '../../lists/Removables';
  import { isStr, isNum, debounce } from '../../../utils';

  const debouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      Removables
    },
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        manager: {
          required,
        },
        country: {
          required,
        },
        assign: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Add manager',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        manager: null,
        country: null,
        assign: null,
        assignItems: [
          {
            name: 'Yes',
            isOn: true,
            isSelected: false,
          },
          {
            name: 'No',
            isOn: false,
            isSelected: true,
          },
        ],
        managerSearch: '',
        isLoading: false,
        isLoadingManagers: false,
        isRefreshingManagers: false,
      };
    },
    computed: {
      managers() {
        return this.$store.getters['users/managers'];
      },
      countries() {
        return this.$store.getters['users/countries'];
      },
      accountManagers() {
        const { accountManagers } = this.$store.getters['businesses/activeItem'] || {};
        return accountManagers || [];
      },
      hasItems() {
        return this.accountManagers.length > 0;
      },
      managerErrors() {
        return this.v$.manager.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Manager is required';
          }
          return error.$message;
        });
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id'(nextId) {
        if (nextId) {
          this.clear();
          this.getAccountManagers(this.business);
        }
      },
      manager() {
        this.isRefreshingManagers = true;
      },
      managerSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingManagers) {
            this.isRefreshingManagers = false;
            return false;
          }

          debouncer(() => this.getManagers(nextSearch));
        }
      },
    },
    created() {
      this.getAccountManagers(this.business);
      this.assignSelectedItems();
    },
    methods: {
      assignSelectedItems() {
        this.assign = this.assignItems.find((item) => item.isSelected);
      },
      getManagers(search = '') {
        const query = {
          search,
        };

        this.isLoadingManagers = true;
        return this.$store.dispatch('users/getManagers', query).then((res) => {
          this.isLoadingManagers = false;
          return res;
        });
      },
      onUpdate() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }
        this.isLoading = true;
        const { manager, country, assign, onSubmit } = this;

        onSubmit({
          manager,
          country,
          assign: assign.isOn,
        }).then((res)=>{
          this.getAccountManagers(this.business);
          return res;
        }).catch(err=>err)
        .finally(()=>{
          this.isLoading = false;
        });
      },
      canSubmitRequest() {
        const { manager, country, assign } = this;

        return isNum(manager?.id) && isStr(country?.code) && isStr(assign?.name);
      },
      getAccountManagers({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getAccountManagers', query);
      },
      onRemove(item) {
        const query = {
          id: this.business.id,
          manager: item.manager.id,
          country: item.manager_country.code,
        };

        return this.$store.dispatch('businesses/removeAccountManager', query);
      },
      clear() {
        this.v$.$reset();
        this.country = null;
        this.weeklySubmissions = '';
        this.dailySubmissions = '';
        this.managerSearch = '';
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='account-managers'] {
  padding: $spacing-xl;
  background-color: rgb(var(--v-theme-surface));
  border-top: 1px solid map.get($colors, grey, lighten-2);

  // The selects sit in a v-row without columns; on a phone they stack.
  .component-header > .v-row {
    @include phone {
      flex-direction: column;
      margin: 0;

      > div {
        width: 100%;
      }
    }
  }

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  .v-list-item {
    border-radius: $border-radius;
    background-color: rgba(var(--v-theme-on-surface), 0.06);
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: rgba(var(--v-theme-on-surface), 0.06);
    }
  }
}
</style>
